import clsx from "clsx";
import { type ChangeEvent, type ComponentProps, useCallback } from "react";

import { Button } from "@shared/ui/button";
import { Body, Heading } from "@shared/ui/typography";

import type { ProgramQuestionsType } from "@/_pages/ai-assistant/model/types";

import s from "./steps.module.scss";

export type CompProps = ComponentProps<"div"> & {
  className?: string;
  disabled: boolean;
  step: string;
  data: ProgramQuestionsType;
  answer?: string | string[];
  onChangeAnswer: (step: string, answer: string | string[]) => void;
};

export const Step = (props: CompProps) => {
  const { className, disabled, step, data, answer, onChangeAnswer } = props;

  const answers = data.answers.map((item) => item.answerValue);
  const selectedAnswers = Array.isArray(answer)
    ? answer
    : answer
      ? [answer]
      : [];
  const hasAnswers = answers.length > 0;

  const handleClickOnButton = useCallback(
    (item: string) => {
      if (data.oneOrMany) {
        const next = selectedAnswers.includes(item)
          ? selectedAnswers.filter((current) => current !== item)
          : [...selectedAnswers, item];
        onChangeAnswer(step, next);
        return;
      }

      onChangeAnswer(step, item);
    },
    [data.oneOrMany, onChangeAnswer, selectedAnswers, step],
  );

  const handleInputChange = useCallback(
    ({ target }: ChangeEvent<HTMLInputElement>) => {
      onChangeAnswer(step, target.value);
    },
    [onChangeAnswer, step],
  );

  return (
    <div className={clsx(s.root, className)}>
      <Heading level={4} className={s.title}>
        {data.question}
      </Heading>
      {!hasAnswers && (
        <div className={clsx(s.inputWrap, { [s.disabled]: disabled })}>
          <input
            type="text"
            placeholder="Напишите свой вариант"
            className={s.input}
            value={Array.isArray(answer) ? "" : (answer ?? "")}
            onChange={handleInputChange}
          />
        </div>
      )}

      {hasAnswers && (
        <>
          <Body className={s.text}>
            {data.oneOrMany
              ? "Выберите один или несколько вариантов"
              : "Выберите один вариант"}
          </Body>
          <div className={s.buttons}>
            {answers.map((item) => (
              <Button
                key={item}
                onClick={() => handleClickOnButton(item)}
                className={clsx(s.button, {
                  [s.active]: selectedAnswers.includes(item),
                  [s.disabled]: disabled,
                })}
              >
                <Body size="caption" weight="semiBold">
                  {item}
                </Body>
              </Button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

Step.displayName = "Step";
